import React, { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowBack } from "react-icons/io";
import { useQuery } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";

import PageHead from "@/components/common/PageHead";
import config from "@/config";
import { getAgentProfileHeader } from "@/services/AgentService";
import { AgentProfile } from "./AgentProfile";
import { UserProfile } from "./UserProfile";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { useUser } from "@/context/UserContext";

export default function Profile() {
    const { nickname } = useParams<{ nickname: string }>();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth0();
    const { userState } = useUser();
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const hasTrackedPageViewRef = useRef<string | null>(null);

    const {
        data: agentHeader,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ["agent-profile-header", nickname],
        queryFn: () => getAgentProfileHeader((nickname ?? "").trim()),
        enabled: !!nickname,
        retry: false,
    });

    const isAgentProfile = !!agentHeader && !isError;
    const profileNickname = (nickname ?? "").trim();

    useEffect(() => {
        if (isLoading || !profileNickname) return;
        const currentKey = `${profileNickname}:${isAgentProfile ? "agent" : "user"}`;
        if (hasTrackedPageViewRef.current === currentKey) return;

        sendMixpanelEvent("Page View", {
            isAuthenticated,
            pageName: isAgentProfile ? "Agent Profile" : "User Profile",
            pageUrl: window.location.href,
            user_id: userState?.id ?? null,
            nickname: userState?.nickname ?? null,
            email: userState?.emailAddress ?? null,
        });
        hasTrackedPageViewRef.current = currentKey;
    }, [isLoading, profileNickname, isAgentProfile, isAuthenticated, userState, sendMixpanelEvent]);

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate("/", { replace: true });
        }
    };

    return (
        <>
            <PageHead
                title={isLoading ? "Loading profile | PriceMe" : "Profile | PriceMe"}
                description="User profile"
                canonicalUrl={`${config.BASE_URL}/profile/${nickname ?? ""}`}
            />

            <div className="tw-max-w-[600px] md:tw-max-w-[1200px] tw-mx-auto tw-min-h-[calc(100vh-9svh)]">
                <button
                    type="button"
                    onClick={handleBack}
                    className="tw-fixed tw-z-[50] tw-bg-black/50 tw-backdrop-blur-md tw-text-white tw-rounded-full tw-shadow-md tw-transition-all tw-flex tw-items-center tw-justify-center tw-p-2 tw-left-2 tw-top-[10svh]"
                    aria-label="Go back"
                >
                    <IoIosArrowBack className="tw-h-6 tw-w-6 md:tw-h-10 md:tw-w-10 lg:tw-h-7 lg:tw-w-7 tw-text-white tw-cursor-pointer" />
                </button>

                {isLoading && (
                    <div className="tw-flex tw-items-center tw-justify-center tw-h-[50vh] tw-text-sm tw-text-[var(--dark-grey)]">
                        Loading profile…
                    </div>
                )}

                {!isLoading && (
                    <>
                        {isAgentProfile ? (
                            <AgentProfile nickname={profileNickname} onBack={handleBack} />
                        ) : (
                            <UserProfile nickname={profileNickname} onBack={handleBack} />
                        )}
                    </>
                )}
            </div>
        </>
    );
}

